import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import swal from 'sweetalert';

import CRMApi from '../../../Api/CRM/CRMApi';
import GetDictionaryResponse from '../../../Api/CRM/Responses/DictionaryResponse';
import Faq from '../../../Api/Models/Faq';
import FaqCategory from '../../../Api/Models/FaqCategory';
import TrueFalseIcon from '../../../components/common/TrueFalseIcon';
import useGlobalState from '../../GlobalStateContext';
import ActionButton from '../../elements/ActionButton';
import Card from '../../elements/common/Card';
import Row, { TableCell as Cell, TableDetailsBody } from '../../elements/common/TableDetailsRow';
import TableHead from '../../elements/common/TableHead';
import TableLoader from '../../elements/common/TableLoader';
import { SelectOptions } from '../../elements/formElements/Select';
import Paginator from '../../elements/paginator';
import { PERMISSION } from '../../helpers/Permissions';
import { handleResponseError } from '../../helpers/ResponseErrorHelper';
import useAutoPaginator, { UpdateItemsFunction } from '../../hooks/useAutoPaginator';
import useNumberParams from '../../hooks/useNumberParams';
import useOnce from '../../hooks/useOnce';

import axios from '../../services/MyAxios';

export default function SingleFaqCategory() {
    const { languageNames } = useGlobalState();
    const { id } = useNumberParams();
    const [faqCategory, setFaqCategory] = useState<FaqCategory>({} as FaqCategory);
    const [translatables, setTranslatables] = useState<GetDictionaryResponse>({});

    useOnce(() => {
        CRMApi.faqCategories.getById(id).then(setFaqCategory);
        CRMApi.faqCategories.getTranslatableNames().then(setTranslatables);
    });

    const { items, updateItems, isLoading, auto } = useAutoPaginator<Faq>((page, params) =>
        CRMApi.faqCategories.getFaqs(id, page, { params })
    );

    return (
        <>
            <Card title="Faq category details">
                <TableDetailsBody>
                    <Row label="ID">{faqCategory.id}</Row>
                    <Row label="Name">{faqCategory.name}</Row>
                    <Row label="Language">{languageNames[faqCategory.language_id]}</Row>
                    <Row label="Translation of">{translatables[faqCategory.translation_id]}</Row>
                </TableDetailsBody>
                <Link to={`/faq_category/${id}/edit`} className="btn btn-default right">
                    Edit Faq Category
                </Link>
            </Card>
            <ActionButton
                url={`/faq_category/${id}/add_faq`}
                title="Add FAQ to category"
                permissions={[PERMISSION.UPDATE_FAQ_CATEGORY]}
            />
            <Card title="Faqs">
                <TableLoader isLoading={isLoading}>
                    <table className="table model-list">
                        <TableHead>
                            <th>ID</th>
                            <th>Order</th>
                            <th>Question</th>
                            <th>Language</th>
                            <th>Translation of</th>
                            <th>Is mobile</th>
                            <th>Is Agent</th>
                            <br />
                        </TableHead>
                        <FaqsTableBody
                            items={items}
                            update={updateItems}
                            languageNames={languageNames}
                            categoryId={id}
                        />
                    </table>
                    <Paginator {...auto.paginator} />
                </TableLoader>
            </Card>
        </>
    );
}

function FaqsTableBody({
    items,
    update,
    languageNames,
    categoryId,
}: {
    items: Faq[];
    update: UpdateItemsFunction;
    languageNames: SelectOptions;
    categoryId: number;
}) {
    return (
        <tbody>
            {items.map((item) => (
                <tr key={item.id}>

                    <td>{item.id}</td>
                    <td>{item.order}</td>
                    <td>{item.question}</td>
                    <td>{languageNames[item.language_id]}</td>
                    <Cell>{item.translation_id}</Cell>
                    <td>
                        <TrueFalseIcon status={item.is_mobile} />
                    </td>
                    <td>
                        <TrueFalseIcon status={item.is_agent} />
                    </td>
                    <td>
                        <span
                            className="btn btn-primary btn-sm square-button"
                            onClick={() => handleRemoveClick(item.id, categoryId, update)}
                        >
                            <i className="fa fa-trash-alt" />
                        </span>
                    </td>
                </tr>
            ))}
        </tbody>
    );
}

function handleRemoveClick(faqId: number, categoryId: number, updateItems: UpdateItemsFunction) {
    swal({
        title: 'Remove FAQ from category',
        text: 'Are you sure you want to remove FAQ from category?',
        icon: 'warning',
        buttons: true as any,
        dangerMode: true,
    }).then((willRemove) => {

        if (willRemove) {
            const requestUrl = `/crea/api/faq_category/${categoryId}/remove_faq`;
            axios
                .post(requestUrl, { faqId })
                .then(() => {
                    updateItems();
                    swal('FAQ has been removed!', {
                        icon: 'success',
                    });
                })
                .catch(handleResponseError);
        }
    });
}
